import React from 'react';
import type { Page, Section } from '../../config/types';
import type { ApprovalRecord, FetchFormResponse } from '../../service/types';
import { SummaryAnswers } from '../SummaryAnswers/SummaryAnswers';
import { formatLondonDateTime } from '../../utils/londonTime';

export type StaffSummarySection = {
  section: Section;
  subsection: Section['subsections'][number];
  pagesForSummary: Page[];
};

export default function ScsStaffForm({
  data,
  sections,
  visible,
  approvals,
  year,
  selectedQuarter,
}: {
  data: FetchFormResponse;
  sections: Section[];
  visible: StaffSummarySection[];
  approvals: Record<string, ApprovalRecord>;
  year: string;
  selectedQuarter: string;
}) {
  return (
    <details className="govuk-details">
      <summary className="govuk-details__summary">
        <span className="govuk-details__summary-text">View staff form</span>
        <span className="govuk-body-s govuk-!-margin-bottom-0" style={{ display: 'block' }}>
          {selectedQuarter === 'Q1'
            ? 'Click here to see the staff form'
            : 'Click here to see the staff form and previous quarter approvals'}
        </span>
      </summary>
      <div className="govuk-details__text">
        {visible.map(({ section, subsection, pagesForSummary }, index) => {
          const quarter = section.quarterReview?.quarter;
          const lastInQuarter =
            quarter && visible[index + 1]?.section.quarterReview?.quarter !== quarter;
          const approval = quarter
            ? Object.values(approvals).find(
                (entry) =>
                  entry.quarter === quarter &&
                  entry.year === year &&
                  entry.status !== 'Rejected' &&
                  entry.approved_at,
              )
            : undefined;
          const blankPerformance =
            pagesForSummary.some(
              (page) =>
                'questions' in page &&
                page.questions?.some(
                  (question) => question.name === `performance-summary-${quarter?.toLowerCase()}`,
                ),
            ) && !data.answers?.[`performance-summary-${quarter?.toLowerCase()}`];
          return (
            <React.Fragment key={subsection.slug}>
              <h3 className="govuk-heading-m">{subsection.title}</h3>
              {blankPerformance && (
                <p className="govuk-body">No performance summary entered yet.</p>
              )}
              <SummaryAnswers
                pages={pagesForSummary}
                subsection={subsection}
                urlPath={`/${section.slug}/${subsection.slug}`}
                data={data}
                disableChangeLinks
              />
              {lastInQuarter && (
                <div style={{ borderLeft: '4px solid #00703c', paddingLeft: 16, marginBottom: 32 }}>
                  <h3 className="govuk-heading-m">{quarter} manager review</h3>
                  {approval ? (
                    <>
                      <p className="govuk-body">
                        Completed {formatLondonDateTime(approval.approved_at)}
                      </p>
                      {sections.flatMap((entry) =>
                        entry.subsections.map((sub) => {
                          const pages = sub.pages
                            .filter((page) => page.name === `approval-${quarter.toLowerCase()}`)
                            .map((page) => ({ ...page, disabled: undefined }));
                          return pages.length ? (
                            <SummaryAnswers
                              key={sub.slug}
                              pages={pages}
                              subsection={sub}
                              urlPath={`/${entry.slug}/${sub.slug}`}
                              data={{ ...data, answers: approval.answers || {}, drafts: {} }}
                              disableChangeLinks
                              includeApprovalPage
                            />
                          ) : null;
                        }),
                      )}
                      {approval.comments && (
                        <p className="govuk-body" style={{ whiteSpace: 'pre-wrap' }}>
                          Notes: {approval.comments}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="govuk-body">No manager review recorded for {quarter}.</p>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </details>
  );
}
